import { defaultsDeep, cloneDeep } from 'lodash'
import Cookies from 'js-cookie'
import type { AxiosRequestConfig, AxiosInstance, AxiosResponse } from 'axios'
import type { Assign } from 'utility-types'
import * as Sentry from '@sentry/react'
import { getLocalLang } from '@/utils'
import { setTraceInfo } from '../reports'
import { SOURCE_SYSTEM } from '@/constants/common'
import { compose } from './compose'
import type { MapTakeLatest } from './extensions'
import { takeLatest } from './extensions'
import { interceptorLoggerHandler } from '@/services/track/logger'
import dayjs from 'dayjs'

/**
 * 因为拦截器将返回参数修正
 * 因此这里需重写部分域的调用函数,
 * 因为TS是声明式, 所以不建议通过JS来实现方法重写
 * 所以有以下重写方式
 */

/** 网关返回 data 数据格式 */
type GatwayData = {
  isSuccess: boolean
  code?: string
  message?: string
  extMap?: any
}

/** 网关返回 body 数据格式 */
type GatwayBody<T extends Record<string, any> = any> = {
  data: Assign<GatwayData, T>
  code: string
  message: string
  bizCode?: string
  bizMessage?: string
  timestamp?: string
  sign?: string
  signType?: string
  channel?: string
  api?: string
  error?: string
}

/** UDB返回 body 数据格式 */
type UdbBody<T extends Record<string, any> = any> = {
  data?: T
  rescode: string
  resmsg: string
  stoken?: string
}

/**
 * sentry 上报
 * @param rejection
 * @param response
 */
const sentryReport = (rejection: Error | Record<string, any>, response: AxiosResponse<any>) => {
  const { config, data, status } = response || {}
  const { url, method } = config || {}
  const { traceparent } = config?.headers || {}
  const { code, message } = data || {}
  const error = rejection instanceof Error ? rejection : new Error(rejection.message)

  const traceId = traceparent?.split('-')[1]

  Sentry.captureException(error, (scope) => {
    scope.setTag('errorType', 'xhr')
    scope.setTag('traceId', traceId)
    scope.setExtras({ traceId, code, message })
    scope.addBreadcrumb({
      type: 'error',
      category: 'xhr',
      level: Sentry.Severity.Error,
      message: `${method.toUpperCase()} ${url} [${status}]`,
      data: {
        traceId,
      },
    })

    return scope
  })
}

/**
 * 因为每个域都可能有所不同,
 * 因此这里使用可以根据配置来对
 * 不同的域名进行不同的操作
 *
 * 如果出现结果处理或请求数据`在该特定域名下`需要特殊处理
 * 这里可以引入拦截器 (inteceptor) 配置;
 *
 * @example
 * export default {
 *   api: {
 *     interceptors ({ request, response }) {
 *        // 方式跟 ajax 添加拦截器一样
 *        request.use(...)
 *        response.use(...)
 *     }
 *   }
 * }
 *
 * * (注意) 如果是默认配置的修改可以直接在
 * * `@/services/http/create.js` 进行全局修改
 */

/**
 * whileList for which don't need the langauge params
 */
const whilteList = ['/api/addresses/list']

export const handleInterceptor = (interceptor: AxiosInstance['interceptors'], extInfo?: Record<string, any>) => {
  interceptorLoggerHandler(interceptor)
  interceptor.request.use(async (requestOptions: AxiosRequestConfig) => {
    const finalOptions = cloneDeep({
      ...requestOptions,
      ...(extInfo ? extInfo : {}),
    })

    // 接入trace
    setTraceInfo(finalOptions)
    // 因为用户可能改设置，所以这里改成实时读
    const localLang = getLocalLang()?.[0]?.languageType || 'en'
    const timezone = dayjs.tz.guess()
    finalOptions.data = defaultsDeep(finalOptions.data, { language: localLang, sourceSystem: SOURCE_SYSTEM, timezone: timezone })
    if (['get', 'GET'].includes(finalOptions.method)) {
      finalOptions.params = defaultsDeep(finalOptions.params, { language: localLang, sourceSystem: SOURCE_SYSTEM, timezone: timezone })
    }

    /**
     * 如果是白名单中的接口, 则不需要添加语言参数
     */
    if (whilteList.includes(finalOptions.url)) {
      delete finalOptions.params?.language
      delete finalOptions.data?.language
    }
    // 超管用户需要添加header
    const authorization = Cookies.get('authorization')
    if (authorization) {
      finalOptions.headers.authorization = authorization
    }

    return finalOptions
  })

  interceptor.response.use(
    (response: AxiosResponse) => {
      const result = (() => {
        const data = response.data
        if (typeof data === 'undefined') {
          return Promise.reject(new Error('Failed to obtain data'))
        }

        const { code } = data

        if (code === 'SUCCESS') {
          return data
        }

        if (code === 'DATA_NOT_FOUND') {
          return {
            ...data,
            code: 'SUCCESS',
            data: null,
          }
        }

        return Promise.reject({
          ...data,
          config: response.config,
        })
      })()

      if (typeof result?.catch === 'function') {
        return result.catch((rejection: Error | Record<string, any>) => {
          sentryReport(rejection, response)
          return Promise.reject(rejection)
        })
      }

      return result
    },
    (rejection) => {
      sentryReport(rejection, rejection?.response)
      return Promise.reject(rejection)
    }
  )
}

const generate = compose({
  admin: {
    interceptors(interceptors: AxiosInstance['interceptors']) {
      handleInterceptor(interceptors)
    },
    extensions: {
      takeLatest: takeLatest,
    },
  },
  udb: {
    interceptors(interceptors: AxiosInstance['interceptors']) {
      interceptorLoggerHandler(interceptors)
      interceptors.response.use((response: AxiosResponse) => {
        const { config, data, status } = response || {}
        const { url, method } = config || {}
        const { rescode, resmsg } = data || {}
        // 成功
        if (rescode === '0') {
          return data
        }

        Sentry.captureException(new Error(resmsg), (scope) => {
          scope.setTag('errorType', 'xhr')

          scope.addBreadcrumb({
            type: 'error',
            category: 'xhr',
            level: Sentry.Severity.Error,
            message: `${method.toUpperCase()} ${url} [${status}]`,
          })

          return scope
        })

        return Promise.reject(data)
      })
    },
  },
  // 全局提示的接口
  toast: {
    interceptors(inteceptor: AxiosInstance['interceptors']) {
      handleInterceptor(inteceptor, {
        toast: true,
      })
    },
    extensions: {
      takeLatest: takeLatest,
    },
  },
  // 不需要全局提示的接口
  noToast: {
    interceptors(inteceptor: AxiosInstance['interceptors']) {
      handleInterceptor(inteceptor)
    },
    extensions: {
      takeLatest: takeLatest,
    },
  },
})
type GenerateBody = {
  admin: GatwayBody<unknown>
  udb: UdbBody<unknown>
  toast: GatwayBody<unknown>
  noToast: GatwayBody<unknown>
}

type GenerateChains = {
  admin: ['data']
  udb: ['data']
  toast: ['data']
  noToast: ['data']
}

type GenerateExtends = MapTakeLatest<GenerateBody, GenerateChains>

/** 最终 http 实例 */
// prettier-ignore
const http = generate<GenerateBody, GenerateChains, GenerateExtends>()
export default http
